import { nanoid } from "@reduxjs/toolkit";
import { useContext, useState } from "react";
import {
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
  Validate,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import FormTable from "../../../common/components/functional/formTable/FormTable";
import isEmpty from "../../../common/helpers/isEmpty";
import { getCoreUserFullName } from "../../../common/utils/users";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { selectCustomerUsersByCompanyId } from "../customerUsersSlice";
import { CustomerUser } from "../types/queries";
import { CustomerContact, ModalState } from "../types/types";
import UserForm from "../users/UserForm";
import { CoreUserType } from "../../user/types/entities";

type Props = {
  customerCompanyId: string;
};

const CustomerContacts = ({ customerCompanyId }: Props) => {
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const { t } = useTranslation();

  const { canEditCustomer } = useContext(AccessRightContext);

  const defaultValues: CustomerContact = {
    firstName: "",
    lastName: "",
    phone: "",
    emailAddress: "",
    title: "",
  };

  const { watch, setValue } = useFormContext();

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const customerContacts: CustomerUser[] = watch("customerContacts");
  const userIdsToBeCreated: string[] = watch("userIdsToBeCreated");
  const userIdsToBeUpdated: string[] = watch("userIdsToBeUpdated");

  const tableData = customerContacts.map((customerUser) => {
    return {
      values: {
        name: getCoreUserFullName(customerUser),
        title: customerUser.account.title,
        phone: customerUser.account.phone,
        emailAddress: customerUser.account.emailAddress,
      },
      deletable: true,
      editable: true,
      id: customerUser.id,
    };
  });

  const customerUsersByCompanyId = useAppSelector(
    selectCustomerUsersByCompanyId(customerCompanyId),
  );

  // customer company users that are not yet added as customer contacts
  const availableCustomerContacts = customerUsersByCompanyId.filter(
    (customerUser) =>
      !customerContacts.some(
        (customerContact) => customerContact.id === customerUser.id,
      ),
  );

  const headers = [
    { text: t("common:name") },
    { text: t("customers:companyInfo:title") },
    { text: t("common:phone") },
    { text: t("common:email") },
  ];

  const onOpen = (id?: string) => setModalState({ show: true, id });
  const onClose = () => setModalState({ show: false, id: "" });

  const onDelete = (id: string) => {
    const userDeleted = customerContacts.filter(
      (customerContact) => customerContact.id !== id,
    );

    setValue("customerContacts", userDeleted, { shouldDirty: true });

    if (userIdsToBeCreated.includes(id)) {
      setValue(
        "userIdsToBeCreated",
        userIdsToBeCreated.filter((userId: string) => userId !== id),
      );
    }

    if (userIdsToBeUpdated.includes(id)) {
      setValue(
        "userIdsToBeUpdated",
        userIdsToBeUpdated.filter((userId: string) => userId !== id),
      );
    }
  };

  const onSave = (customerContact: any) => {
    const existingCustomerUser = customerUsersByCompanyId.find(
      (customerUser) => customerUser.id === customerContact.id,
    );

    if (existingCustomerUser) {
      // add or edit existing user
      const updatedCustomerContact: CustomerUser = {
        id: existingCustomerUser.id,
        type: CoreUserType.CUSTOMER,
        account: {
          ...existingCustomerUser.account,
          ...customerContact,
        },
      };

      if (
        !userIdsToBeUpdated.includes(updatedCustomerContact.id) &&
        !userIdsToBeCreated.includes(updatedCustomerContact.id)
      ) {
        setValue("userIdsToBeUpdated", [
          ...userIdsToBeUpdated,
          updatedCustomerContact.id,
        ]);
      }

      let updatedCustomerContacts = [];

      if (modalState.id) {
        // update existing user in form state
        updatedCustomerContacts = customerContacts.map(
          (currentCustomerContact) =>
            currentCustomerContact.id === existingCustomerUser.id
              ? updatedCustomerContact
              : currentCustomerContact,
        );
      } else {
        // add existing user to form state, email can get updated here
        updatedCustomerContacts = [...customerContacts, updatedCustomerContact];
      }

      setValue("customerContacts", updatedCustomerContacts, {
        shouldDirty: true,
      });
      onClose();
    } else {
      // New user

      if (userIdsToBeCreated.includes(customerContact.id)) {
        // Update existing user to be created
        const updatedCustomerContact: CustomerUser = {
          id: customerContact.id,
          type: CoreUserType.CUSTOMER,
          account: {
            ...customerContact,
          },
        };

        const updatedCustomerContacts = customerContacts.map(
          (currentCustomerContact) =>
            currentCustomerContact.id === customerContact.id
              ? updatedCustomerContact
              : currentCustomerContact,
        );

        setValue("customerContacts", updatedCustomerContacts, {
          shouldDirty: true,
        });
      } else {
        // Generate a temporary id for the new user
        const newTemporaryId = nanoid();
        setValue("userIdsToBeCreated", [...userIdsToBeCreated, newTemporaryId]);

        const formattedCustomerContact: CustomerUser = {
          id: newTemporaryId,
          type: CoreUserType.CUSTOMER,
          account: {
            ...customerContact,
          },
        };

        const updatedCustomerContacts = [
          ...customerContacts,
          formattedCustomerContact,
        ];

        setValue("customerContacts", updatedCustomerContacts, {
          shouldDirty: true,
        });
      }

      onClose();
    }
  };

  const addButtoLabel = t("customers:companyInfo:addContact");

  const isValid = methods.formState.isValid;

  const requiredFields = methods.watch([
    "firstName",
    "lastName",
    "emailAddress",
  ]);

  const saveDisabled =
    requiredFields.some((field) => isEmpty(field)) ||
    !isEmpty(methods.formState.errors) ||
    !isValid ||
    !methods.formState.isDirty;

  const validateEmailAddress: Validate<string, FieldValues> = (
    emailAddress: string,
  ) => {
    const isDirty = !!methods.formState.dirtyFields["emailAddress"];

    if (!isDirty) return true;

    const existingContact = customerContacts.find(
      (customerUser) => customerUser.account.emailAddress === emailAddress,
    );

    if (existingContact) {
      const emailInUse = t("customers:usersNS:emailInUse");
      return emailInUse;
    }

    return true;
  };

  return (
    <FormSection
      title={t("customers:companyInfo:customerContactPersons")}
      titleInside
      xsLabel={2.8}
      xsField={9.2}
    >
      <FormTable
        data={tableData}
        headers={headers}
        contentKey=""
        handleOpenEditModal={(_, id) => onOpen(id)}
        addButtonLabel={addButtoLabel}
        handleDelete={(id) => onDelete(id)}
        disableEditRow={!canEditCustomer}
      />
      <DialogComponent open={modalState.show} onClose={onClose}>
        <DialogContentWrapper
          margin
          title={t("customers:workLocationNS:addCustomerContactPerson")}
          onCancel={onClose}
          onSave={methods.handleSubmit(onSave)}
          saveDisabled={saveDisabled}
        >
          <FormProvider {...methods}>
            <UserForm
              userId={modalState.id}
              userData={customerContacts.find(
                (user) => user.id === modalState.id,
              )}
              customerUsers={availableCustomerContacts}
              validateEmailAddress={validateEmailAddress}
            />
          </FormProvider>
        </DialogContentWrapper>
      </DialogComponent>
    </FormSection>
  );
};

export default CustomerContacts;
